/**
 * Цвет разметки — одна запись на всё: `#RRGGBB` или `#RRGGBBAA`.
 *
 * Прозрачность живёт в самом цвете (последние две цифры): так её
 * понимают и холст, и CSS, и профиль хранит одну строку, а не пару
 * «цвет + плотность» в разных местах. Все подкрашивания разметки
 * (ореолы, заливки, блики) умножают свою плотность на плотность цвета —
 * полупрозрачная линия и светится вполсилы.
 */

export interface Rgba {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  /** 0…1. */
  readonly a: number;
}

/** `#RGB`, `#RRGGBB`, `#RRGGBBAA` → составляющие; остальное — `null`. */
export function parseColor(color: string): Rgba | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(color.trim());
  if (!m) return null;
  let v = m[1]!;
  if (v.length === 3) v = `${v[0]}${v[0]}${v[1]}${v[1]}${v[2]}${v[2]}`;
  return {
    r: Number.parseInt(v.slice(0, 2), 16),
    g: Number.parseInt(v.slice(2, 4), 16),
    b: Number.parseInt(v.slice(4, 6), 16),
    a: v.length === 8 ? Number.parseInt(v.slice(6, 8), 16) / 255 : 1,
  };
}

const hex2 = (value: number): string => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0');

/** Составляющие → `#RRGGBB`, а с прозрачностью меньше 100% — `#RRGGBBAA`. */
export function formatColor({ r, g, b, a }: Rgba): string {
  const base = `#${hex2(r)}${hex2(g)}${hex2(b)}`.toUpperCase();
  return a >= 0.999 ? base : `${base}${hex2(a * 255)}`.toUpperCase();
}

/** Тот же цвет без прозрачности. */
export function solidOf(color: string): string {
  const c = parseColor(color);
  return c ? formatColor({ ...c, a: 1 }) : color;
}

/** Плотность цвета, 0…1. */
export function opacityOf(color: string): number {
  return parseColor(color)?.a ?? 1;
}

/** Тот же цвет с другой плотностью. */
export function withOpacity(color: string, opacity: number): string {
  const c = parseColor(color);
  return c ? formatColor({ ...c, a: Math.min(1, Math.max(0, opacity)) }) : color;
}

/**
 * Подкрашивание: цвет с плотностью `alpha`, умноженной на собственную.
 *
 * Не понятый разбором цвет возвращается как есть — холст сам разберётся
 * с `rgb()` и именами цветов.
 */
export function withAlpha(color: string, alpha: number): string {
  const c = parseColor(color);
  if (!c) return color;
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${+Math.min(1, alpha * c.a).toFixed(3)})`;
}

/** Одинаковые ли цвета — без разницы в регистре и записи прозрачности. */
export function sameColor(a: string, b: string): boolean {
  const x = parseColor(a);
  const y = parseColor(b);
  if (!x || !y) return a.toLowerCase() === b.toLowerCase();
  return x.r === y.r && x.g === y.g && x.b === y.b && Math.abs(x.a - y.a) < 0.004;
}

/* --------------------------------------------------------- модель HSV */

export interface Hsv {
  /** 0…360. */
  readonly h: number;
  /** 0…1. */
  readonly s: number;
  /** 0…1. */
  readonly v: number;
}

export function rgbToHsv({ r, g, b }: Rgba): Hsv {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === R) h = ((G - B) / d) % 6;
    else if (max === G) h = (B - R) / d + 2;
    else h = (R - G) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv, a = 1): Rgba {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255, a };
}

/* ------------------------------------------------------------ палитра */

/** Серый ряд — от белого к чёрному, с лёгким холодным оттенком нашего фона. */
export const GRAYS: readonly string[] = [
  '#FFFFFF',
  '#E7EAF0',
  '#C6CCD7',
  '#9FA7B6',
  '#7A8395',
  '#5A6374',
  '#3E4656',
  '#272E3C',
  '#161B26',
  '#000000',
];

/** Яркий ряд — фирменные тона ANZONIX. */
export const HUES: readonly string[] = [
  '#FF5470',
  '#FF8A3D',
  '#F5C04E',
  '#2EE6B0',
  '#3FBF87',
  '#5AC8D8',
  '#3BA7FF',
  '#6E8BFF',
  '#A78BFA',
  '#F472B6',
];

function mix(color: string, toward: number, share: number): string {
  const c = parseColor(color)!;
  const blend = (v: number): number => v + (toward - v) * share;
  return formatColor({ r: blend(c.r), g: blend(c.g), b: blend(c.b), a: 1 });
}

/**
 * Оттенки ярких тонов: три светлее и три темнее, от светлого к тёмному —
 * как в привычных редакторах, только в нашей гамме.
 */
export const SHADES: readonly (readonly string[])[] = [
  HUES.map((hue) => mix(hue, 255, 0.78)),
  HUES.map((hue) => mix(hue, 255, 0.58)),
  HUES.map((hue) => mix(hue, 255, 0.32)),
  HUES.map((hue) => mix(hue, 0, 0.22)),
  HUES.map((hue) => mix(hue, 0, 0.42)),
  HUES.map((hue) => mix(hue, 0, 0.6)),
];
